import { parsePhoneNumber } from 'libphonenumber-js'
import { equals, includes, isEmpty, isNil, length, replace, split } from 'ramda'
import { times } from '~/constants/config'
import { i18nConfig } from '~/i18n.config'

import {
  formatDateString,
  formatWeekdayString,
  isDate,
  isToday,
  isWithinLastWeek,
  isYesterday,
} from '@mangovoice/core'

function capitalizeString(str: string): string {
  return `${str.charAt(0).toUpperCase()}${str.slice(1)}`
}

function toNounCasing(str: string): string {
  //uppercase the first letter, lowerize all others, split on space
  return str
    .split(' ')
    .map((word) => {
      //special case for abbreviations
      //capitalized abbrevation
      if (word.length < 4 && word.toUpperCase() === word) {
        return word
      }

      //abbreviation with a period already noted
      if (word.length <= 4 && word.endsWith('.')) {
        return word
      }

      //word can be noun-casified
      return `${word.charAt(0).toUpperCase()}${word.slice(1).toLowerCase()}`
    })
    .join(' ')
}

function formatPhoneNumber1(phoneNumberString?: string): string {
  if (!phoneNumberString) return ''

  const cleaned = `${phoneNumberString}`.replace(/\D/g, '')
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`
  }
  return `${phoneNumberString}`
}

function formatPhoneNumber2(phoneNumberString: string): string {
  const match = split(' ', `${phoneNumberString}`)
  if (match.length > 0) {
    let numberString
    if (match[2]) {
      numberString = `(${match[0]}) ${match[1]}-${match[2]}`
    } else {
      numberString = `${match[0]}-${match[1]}`
    }
    return numberString
  }
  return `${phoneNumberString}`
}

const phoneFormatter = (phone) => {
  // Remove +1 for e164 numbers.
  if (isNil(phone)) {
    return ''
  }

  if (phone.length > 10) {
    phone = phone.substring(phone.length - 10)
  }

  let phoneNumber: string

  try {
    const number = parsePhoneNumber(phone).formatNational()
    phoneNumber = formatPhoneNumber2(number)
  } catch (error) {
    phoneNumber = formatPhoneNumber1(phone)
  }
  return phoneNumber
}

const templatePhoneNumber = (phoneNumber) => {
  return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(
    3,
    6,
  )}-${phoneNumber.substring(6)}`
}

export const formatPhoneNumber = (phoneNumberString, noPrefex = false) => {
  if (
    typeof phoneNumberString !== 'string' ||
    isEmpty(phoneNumberString || isNil(phoneNumberString))
  ) {
    return ''
  }

  if (
    !noPrefex &&
    (equals(phoneNumberString.charAt(0), '#') ||
      equals(phoneNumberString.charAt(0), '*'))
  ) {
    const cleaned = `${phoneNumberString}`.replace(/\D/g, '')
    return `${phoneNumberString.charAt(0)}${cleaned}`
  }

  const cleaned = `${phoneNumberString}`.replace(/\D/g, '')

  if (cleaned.length <= 6) {
    return cleaned
  } else if (cleaned.length === 7) {
    return `${cleaned.substring(0, 3)}-${cleaned.substring(3, 7)}`
  } else if (cleaned.length >= 8 && cleaned.length <= 10) {
    return templatePhoneNumber(cleaned)
  }

  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return templatePhoneNumber(cleaned.substring(1))
  }

  //just return the cleaned number
  return cleaned
}

const getValidNumber = (phone: string | undefined | null) => {
  if (!phone) return ''
  const destination = replace(/\D+/g, '', phone)
  if (equals(phone.charAt(0), '*')) {
    return `*${destination}`
  }
  // If the number starts with 1 and is 11 digits, remove the leading 1
  if (destination.length === 11 && destination.startsWith('1')) {
    return destination.substring(1)
  }
  return destination
}

/**
 * Formats a date into a localized relative representation.
 *
 * @param dateString - A Date object or string representing a date
 * @returns A localized string:
 *  - "Today" or its localized equivalent if the date is today
 *  - "Yesterday" or its localized equivalent if the date is yesterday
 *  - Weekday name if within the last week
 *  - Formatted date string (e.g., "05/20/23") otherwise
 *  - "Invalid Date" if parsing fails
 *
 * @example
 * formatRelativeDay(new Date()) // Returns "Today" (localized)
 * formatRelativeDay("2023-05-19") // Returns "Yesterday" (localized) if today is 2023-05-20
 */
export const formatRelativeDay = (dateString: string | Date) => {
  try {
    const date = new Date(dateString)
    if (!isDate(date)) {
      console.error('dateFormatter: Invalid date string provided', { date })
      return 'Invalid Date'
    }
    // const date = getValidDate(dateString)

    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (isToday(date)) {
      return i18nConfig.t('today')
    }

    if (isYesterday(date)) {
      return i18nConfig.t('yesterday')
    }

    if (isWithinLastWeek(date)) {
      return formatWeekdayString(date)
    }

    return formatDateString(date)
  } catch (error) {
    console.error('LibError: Failed to format date', { dateString, error })
    return 'Invalid Date'
  }
}

const convertCallDuration = (durationInSeconds: number) => {
  if (durationInSeconds === 0) return '0:00'
  const seconds = durationInSeconds % times.secsOfMin
  const mins = Math.floor(durationInSeconds / times.secsOfMin)
  const displayMins = mins % times.minsOfHour
  const hours = Math.floor(mins / times.minsOfHour)
  let duration = ''
  if (mins >= times.minsOfHour) {
    duration = `${hours}:${displayMins < 10 ? '0' : ''}${displayMins}:${
      seconds < 10 ? '0' : ''
    }${seconds}`
  } else if (mins > 0) {
    duration = `${mins}:${seconds < 10 ? '0' : ''}${seconds}`
  } else {
    duration = `0:${seconds < 10 ? '0' : ''}${seconds}`
  }
  return duration
}
const convertCallDurationTruncateMilliseconds = (durationInSeconds: number) => {
  let duration = convertCallDuration(durationInSeconds)
  if (duration.includes('.')) {
    const [toSeconds, milliseconds] = duration.split('.')
    const truncactedMilliseconds = milliseconds.substring(0, 3)
    duration = `${toSeconds}.${truncactedMilliseconds}`
  }
  return duration
}
const convertCallDurationNoMilliseconds = (durationInSeconds: number) => {
  let duration = convertCallDuration(durationInSeconds)
  if (duration.includes('.')) {
    duration = duration.split('.')[0]
  }
  return duration
}

const tripLeadingZeroFromTime = (time) => {
  const parts = time.split(':')
  while (parts.length > 2 && parts[0] === '00') {
    parts.shift()
  }
  parts[0] = parts[0].replace(/^0+/, '') || '0'
  return parts.join(':')
}

const convertVoicemalLength = (d) => {
  const seconds = Math.round(d % times.secsOfMin)
  const mins = Math.floor(d / times.secsOfMin)
  const duration = `${mins}:${seconds < 10 ? '0' : ''}${seconds}`
  return duration
}

const convertTypingLabel = (typerData) => {
  if (length(typerData) === 0) return null
  let typerName = ''
  if (length(typerData) > 2) {
    typerName = `${length(typerData)} people`
  } else if (length(typerData) > 1) {
    typerName = `${typerData[0]}, ${typerData[1]}`
  } else {
    typerName = `${typerData[0]}`
  }
  return `${typerName} ${length(typerData) > 1 ? 'are' : 'is'} typing...`
}

const validateEmail = (email) => {
  if (includes('@', email)) {
    const re =
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line no-useless-escape
    return re.test(String(email).toLowerCase())
  }
  return true
}

const cleanInvalidString = (input: string) => {
  let output = ''
  for (let i = 0; i < input.length; i += 1) {
    if (input.charCodeAt(i) <= 127) {
      output += input.charAt(i)
    }
  }
  return output
}

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    if (!file) {
      resolve(null)
    } else {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    }
  })

const dataURLtoFile = (dataurl, filename) =>
  new Promise((resolve) => {
    if (!dataurl) {
      resolve(null)
    } else if (dataurl.name && typeof dataurl.name === 'string') {
      resolve(dataurl)
    } else {
      // eslint-disable-next-line one-var
      const arr = dataurl.split(','),
        mime = arr[0].match(/:(.*?);/)[1],
        bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      // eslint-disable-next-line no-plusplus
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n)
      }
      const file = new File([u8arr], filename, { type: mime })
      resolve(file)
    }
  })

const convertFileSize = (size) => {
  const filesize = Math.round(size / 1024)
  return `${filesize} KB`
}

const getInitials = (nameStr) => {
  const anyCharOrNumber = /[A-Za-zÀ-ÖØ-öø-ÿ0-9]/g
  const nonValidTokens = /[^A-Za-zÀ-ÖØ-öø-ÿ0-9\s]/g
  const beginsWithThreeDigits = /^\d{3}/
  // check degenerates
  if (typeof nameStr !== 'string' || isEmpty(nameStr) || isNil(nameStr)) {
    return ''
  }
  // if contains any character or number
  if (anyCharOrNumber.test(nameStr)) {
    const names = nameStr.replace(nonValidTokens, '').split(' ')
    if (names.length === 1) {
      if (beginsWithThreeDigits.test(names[0])) {
        return names[0].substring(0, 3)
      }
      return names[0].charAt(0)
    } else if (names.length > 1) {
      if (beginsWithThreeDigits.test(names[0])) {
        return names[0].substring(0, 3)
      }
      return `${names[0].charAt(0)}${names[1].charAt(0)}`
    }
  } else {
    return ''
  }
}

const getNormalizedName = (name: string): string => {
  if (!name) return name
  const words = name.split(' ')
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

const getFormattedTimeDigits = (digits) => {
  return `${digits < 10 ? `0` : ``}${digits}`
}

const formatSeconds = (seconds: number) => {
  const hours = (seconds / 3600) | 0
  let remainingSeconds = seconds - hours * 3600
  const minutes = (remainingSeconds / 60) | 0
  remainingSeconds = (remainingSeconds - minutes * 60) | 0
  if (hours > 0) {
    return `${getFormattedTimeDigits(hours)}:${getFormattedTimeDigits(minutes)}:${getFormattedTimeDigits(
      remainingSeconds,
    )}`
  } else {
    return `${getFormattedTimeDigits(minutes)}:${getFormattedTimeDigits(
      remainingSeconds,
    )}`
  }
}

const formatMilliseconds = (milliseconds) => {
  const seconds = milliseconds / 1000
  return formatSeconds(seconds)
}

const getFormattedSnippetShortcut = (value: string) => {
  //only letters, numbers and dashes
  const regex = /[^a-zA-Z0-9-]*$/
  return value.replace(regex, '')
}

export {
  phoneFormatter,
  getValidNumber,
  cleanInvalidString,
  convertCallDuration,
  convertCallDurationTruncateMilliseconds,
  convertCallDurationNoMilliseconds,
  convertVoicemalLength,
  convertTypingLabel,
  convertFileSize,
  dataURLtoFile,
  fileToBase64,
  validateEmail,
  getInitials,
  getNormalizedName,
  getFormattedTimeDigits,
  getFormattedSnippetShortcut,
  formatMilliseconds,
  formatSeconds,
  capitalizeString,
  toNounCasing,
  tripLeadingZeroFromTime,
}
